import { z } from "zod";

import { BiologicalSexSchema } from "./targets";

// Child profiles (ADR-0018). Household-scoped minor profiles with kcal /
// macro tracking. The age is computed from dateOfBirth at read time so the
// data never goes stale; height/weight are point-in-time snapshots the
// parent updates manually (no growth history in v1).
//
// Reuses BiologicalSexSchema from targets — same enum as for adult users,
// so the Henry estimator and the Mifflin-St Jeor adult estimator pull from
// one source of truth.

export const ChildActivityLevelSchema = z.enum([
  "sedentary",
  "light",
  "moderate",
  "high",
  "very-high",
]);
export type ChildActivityLevel = z.infer<typeof ChildActivityLevelSchema>;

// Returned from GET /children. Server-shape (timestamps as numbers).
export const ChildSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(80),
  // ISO YYYY-MM-DD. Stored as text in SQLite; service-layer computes age.
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format YYYY-MM-DD"),
  biologicalSex: BiologicalSexSchema,
  // Optional bio measurements. Null when not yet provided; Henry estimator
  // requires both heightCm AND weightKg to produce a kcal target.
  heightCm: z.number().min(40).max(220).nullable(),
  weightKg: z.number().min(5).max(150).nullable(),
  activityLevel: ChildActivityLevelSchema.nullable(),
  // Manual kcal-target override; wins over Henry estimate when set.
  kcalTargetOverride: z.number().int().min(500).max(5000).nullable(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type Child = z.infer<typeof ChildSchema>;

// POST /children body. Only name + dateOfBirth + sex are required at
// creation; the rest can be filled later via PATCH.
export const ChildCreateSchema = z.object({
  name: z.string().min(1).max(80),
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format YYYY-MM-DD"),
  biologicalSex: BiologicalSexSchema,
  heightCm: z.number().min(40).max(220).optional(),
  weightKg: z.number().min(5).max(150).optional(),
  activityLevel: ChildActivityLevelSchema.optional(),
  kcalTargetOverride: z.number().int().min(500).max(5000).optional(),
});
export type ChildCreate = z.infer<typeof ChildCreateSchema>;

// PATCH /children/:id body — all fields optional + nullable for explicit
// clearing of measurements / override.
export const ChildUpdateSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  biologicalSex: BiologicalSexSchema.optional(),
  heightCm: z.number().min(40).max(220).nullable().optional(),
  weightKg: z.number().min(5).max(150).nullable().optional(),
  activityLevel: ChildActivityLevelSchema.nullable().optional(),
  kcalTargetOverride: z.number().int().min(500).max(5000).nullable().optional(),
});
export type ChildUpdate = z.infer<typeof ChildUpdateSchema>;

// Derived target (Henry 2005 + macro split). Returned by GET /children/:id/targets.
export const ChildTargetSchema = z.object({
  // Computed at read time from dateOfBirth.
  ageYears: z.number().int().min(0),
  // null when Henry inputs (height + weight + activity) are incomplete and
  // no manual override is set. UI prompts the user to fill the missing data.
  kcalTarget: z.number().int().nullable(),
  proteinTargetG: z.number().int().nullable(),
  fatTargetG: z.number().int().nullable(),
  carbsTargetG: z.number().int().nullable(),
  // Tells the UI where this target came from.
  source: z.enum(["henry", "override", "incomplete"]),
});
export type ChildTarget = z.infer<typeof ChildTargetSchema>;
